import { format } from 'date-fns';
import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import { StatusChip } from 'components/referrals/referralsTableColumns';
import { capitalize } from 'lib/util';
import LocationEpisode from 'models/LocationEpisode';
import { ReviewStatus } from 'models/Review';
import colors from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

const columnHelper = createColumnHelper<LocationEpisode>();

type Props = {
  onClickPatient: (episode: LocationEpisode) => void;
};

export default function listViewColumns({ onClickPatient }: Props) {
  return [
    columnHelper.accessor((row) => row.patient?.name, {
      id: 'patientName',
      header: 'Patient Name',
      cell: (info) => (
        <PatientLink onClick={() => onClickPatient(info.row.original)} data-dd-action-name='Patient name'>
          {info.getValue()}
        </PatientLink>
      ),
      size: 150,
    }),
    columnHelper.accessor((row) => row.planTypeClassification?.name, {
      id: 'medicarePlan',
      header: 'Plan',
      cell: (info) => {
        const planName = info.getValue();
        const isMedicare = planName?.toLowerCase().includes('medicare');
        return <PlanBadge $isMedicare={!!isMedicare}>{isMedicare ? 'Medicare' : 'Other'}</PlanBadge>;
      },
      size: 100,
    }),
    columnHelper.accessor((row) => row.planTypeClassification?.name, {
      id: 'planTypeClassification',
      header: 'Plan Type',
      cell: (info) => info.getValue() || '—',
      size: 120,
    }),
    columnHelper.accessor((row) => row.projectedDischargeReview?.planningDischargeTo, {
      id: 'projectedDischargePlanningDischargeTo',
      header: 'Planning Discharge To',
      cell: (info) => info.getValue() || '—',
      size: 150,
      sortDescFirst: true,
    }),
    columnHelper.accessor((row) => row.latestReferralReview?.group?.name, {
      id: 'latestReferralGroupName',
      header: 'Referred To',
      cell: (info) => info.getValue() || '—',
      size: 150,
      sortDescFirst: true,
    }),
    columnHelper.accessor((row) => row.latestReferralReview?.status ?? '', {
      id: 'latestReferralStatus',
      header: 'Referral Status',
      cell: (info) => {
        const status = info.getValue() as ReviewStatus | '';
        if (!status) return '—';
        const isRejected = info.row.original.latestReferralReview?.rejected;
        return <StatusChip $status={status}>{isRejected ? 'Denied' : capitalize(status)}</StatusChip>;
      },
      size: 120,
    }),
    columnHelper.accessor((row) => row.lengthOfStay, {
      id: 'lengthOfStay',
      header: 'Length of Stay',
      cell: (info) => {
        const days = info.getValue();
        return days !== undefined ? `${days} days` : '—';
      },
      size: 100,
    }),
    columnHelper.accessor((row) => row.projectedDischargeReview?.date, {
      id: 'projectedDischargeDate',
      header: 'Projected Discharge',
      cell: (info) => {
        const date = info.getValue();
        return date ? format(date, 'MMM d, yyyy') : '—';
      },
      size: 130,
    }),
  ];
}

const PatientLink = styled(BodySmall)`
  cursor: pointer;
  color: ${() => colors.primaryBlue};
  font-weight: 700;
`;

const PlanBadge = styled.span<{ $isMedicare: boolean }>`
  display: inline-block;
  background-color: ${({ $isMedicare, theme }) =>
    $isMedicare ? theme.colors.accentGreen10 : theme.colors.pictonBlue10};
  color: ${({ theme }) => theme.colors.black};
  padding: 4px 8px;
  border-radius: 28px;
  font-size: 12px;
  font-weight: bold;
  line-height: 16px;
  white-space: nowrap;
`;
